import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import T from "../theme/colors";
// Phase 36cg — global double-Escape close for ALL modals using this
// component.  Set disableDoubleEscape={true} on a Modal to opt out
// (e.g. a confirmation dialog where accidental closure would be bad).
import useDoubleEscapeClose from "../hooks/useDoubleEscapeClose";

export default function Modal({
  open, onClose, title, subtitle, children, width = 560, footer, zIndex = 1000,
  disableDoubleEscape = false,
}) {
  useDoubleEscapeClose(onClose, open && !disableDoubleEscape);
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  // Portal renders directly on document.body — no parent overflow clipping ever
  return createPortal(
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: "fixed", inset: 0, zIndex,
        background: "rgba(26,22,18,0.45)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 16px",
        animation: "fadeIn 0.15s ease",
      }}
    >
      <div
        className="modal-drop"
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg-card)",
          border: `1px solid var(--border)`,
          borderRadius: 12,
          width: "100%",
          maxWidth: width,
          maxHeight: "calc(100vh - 40px)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 24px",
          borderBottom: `1px solid var(--border)`,
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          background: "var(--bg-input)",
          borderRadius: "12px 12px 0 0",
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: "var(--text-0)", fontFamily: "var(--font-display)", lineHeight: 1.3 }}>
              {title}
            </h3>
            {subtitle && <p style={{ fontSize: 14, color: "var(--text-1)", marginTop: 3 }}>{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", color: "var(--text-2)",
              cursor: "pointer", display: "flex", padding: 4,
              borderRadius: 6, marginTop: 2, flexShrink: 0,
              transition: "color 0.15s, background 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "var(--text-0)"; e.currentTarget.style.background = "var(--bg-hover)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--text-2)"; e.currentTarget.style.background = "none"; }}
          >
            <X size={17} />
          </button>
        </div>

        {/* Body — only this scrolls */}
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, minHeight: 0 }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            padding: "14px 24px",
            borderTop: `1px solid var(--border)`,
            display: "flex", justifyContent: "flex-end", gap: 8,
            background: "var(--bg-input)",
            borderRadius: "0 0 12px 12px",
            flexShrink: 0,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body   // ← renders outside all page containers
  );
}